import React from "react";

import {
  RESULT_MODE_CONFIG,
  getResultModeConfigForVariant,
} from "../../utils/results/resultModeConfig";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../utils/results/resultModePresentation";
import { buildResultPath } from "../../utils/results/resultNavigation";
import {
  formatPassFailLabel,
  getQualityStatusPillClasses,
  getHeartRateQualityMessage,
  getRespiratoryQualityMessage,
} from "../../utils/results/resultQuality";
import { isPassingHrSignalQuality } from "../../utils/results/signalQuality";

const qualityStatusClasses = {
  pass: "border-primary/20 bg-primary/10 text-primary",
  fail: "border-red-200 bg-red-50 text-red-700",
};

function normalizePassFailStatus(value) {
  const normalized =
    typeof value === "string" ? value.trim().toLowerCase() : null;

  if (!normalized) return null;

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good" ||
    normalized === "very_good" ||
    normalized === "verygood" ||
    normalized === "ok"
  ) {
    return "pass";
  }

  if (
    normalized === "fail" ||
    normalized === "failed" ||
    normalized === "bad" ||
    normalized === "poor" ||
    normalized === "low" ||
    normalized.startsWith("fail")
  ) {
    return "fail";
  }

  return null;
}

function qualityMessageText(message) {
  return [message?.title, message?.subtitle].filter(Boolean).join(" ");
}

function isResultFeatureVisible(resultFeatures, featureKey) {
  if (!resultFeatures || typeof resultFeatures !== "object") {
    return true;
  }

  return resultFeatures?.[featureKey] === true;
}

function QualityStatusChip({
  label,
  status,
  t,
  showStatusLabel = true,
  qualityStatusPillsEnabled = false,
}) {
  if (!status) return null;

  const statusLabel = formatPassFailLabel(status === "fail", t);

  if (qualityStatusPillsEnabled && showStatusLabel) {
    return (
      <span className="inline-flex min-h-5 max-w-full items-center gap-1.5 text-[11px] font-medium leading-tight text-typography">
        <span className="truncate">{label}</span>
        <span
          role="img"
          aria-label={statusLabel}
          title={statusLabel}
          className={[
            "inline-flex h-3 w-8 shrink-0 rounded-full align-middle",
            getQualityStatusPillClasses(status),
          ].join(" ")}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex min-h-5 max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-tight ${
        qualityStatusClasses[status] || qualityStatusClasses.fail
      }`}
    >
      <span className="truncate">{label}</span>
      {showStatusLabel ? (
        <span>{statusLabel}</span>
      ) : null}
    </span>
  );
}

function QualityMessage({ children }) {
  if (!children) return null;

  return (
    <span className="w-full text-xs leading-tight text-greyscale-3">
      {children}
    </span>
  );
}

/**
 * Builds the per-mode quality chips + guidance messages shown in the body of a
 * result list card. Returns `{ chips, messages }` (each an array of JSX nodes)
 * so the caller can render the chips as a wrap row with any guidance messages
 * stacked underneath — the messages are always last, never interleaved.
 */
function buildResultQualityDetails(
  row,
  t,
  resultFeatures = null,
  qualityStatusPillsEnabled = false,
) {
  if (!row) return { chips: [], messages: [] };

  // Collect chips and messages separately so the caller can render the pass/fail
  // chips as a wrap row with any guidance messages stacked underneath — rather
  // than interleaved per quality metric (chip, message, chip, …).
  const chips = [];
  const messages = [];
  const respiratoryQuality = row.rrQuality || row.signalQuality;
  const showSignalQuality = isResultFeatureVisible(resultFeatures, "signalQuality");
  const showSpirometryQuality = isResultFeatureVisible(
    resultFeatures,
    "spirometryQuality",
  );
  const showCardiacQuality = isResultFeatureVisible(
    resultFeatures,
    "cardiacQuality",
  );
  const addChip = (key, label, value) => {
    const status = normalizePassFailStatus(value);
    if (!status) return;

    chips.push(
      <QualityStatusChip
        key={key}
        label={label}
        status={status}
        t={t}
        qualityStatusPillsEnabled={qualityStatusPillsEnabled}
      />,
    );
  };
  const addFailureBadge = (key, label) => {
    if (!label) return;

    chips.push(
      <QualityStatusChip
        key={key}
        label={label}
        status="fail"
        t={t}
        showStatusLabel={false}
      />,
    );
  };
  const addMessage = (key, message) => {
    const text =
      typeof message === "string" ? message : qualityMessageText(message);
    if (!text) return;

    messages.push(<QualityMessage key={key}>{text}</QualityMessage>);
  };

  if (row.uploadFailed) {
    addFailureBadge(
      "upload-failed",
      t("result.uploadFailedTitle", "Upload failed"),
    );
    return { chips, messages };
  }

  if (row.variant === RESULT_MODE_CONFIG.spirometry.variant) {
    if (showSignalQuality) {
      addChip(
        "signal",
        t("result.spiro.signalQuality", "Signal Quality"),
        row.signalQuality,
      );
    }
    if (showSpirometryQuality) {
      addChip(
        "spirometry",
        t("result.spiro.spirometryQuality", "Spirometry Quality"),
        row.spirometryQuality,
      );
    }

    if (
      (showSignalQuality &&
        normalizePassFailStatus(row.signalQuality) === "fail") ||
      (showSpirometryQuality &&
        normalizePassFailStatus(row.spirometryQuality) === "fail")
    ) {
      addMessage(
        "spirometry-quality",
        `${t("result.spiro.qualityFailTitle", "Low signal quality.")} ${t(
          "result.spiro.qualityFailSubtitle",
          "Please try again.",
        )}`,
      );
    }

    return { chips, messages };
  }

  if (row.variant === RESULT_MODE_CONFIG.manualAudio.variant) {
    if (row.manualAudioComplete) {
      addMessage(
        "manual-audio-complete",
        t(
          "result.manualAudio.completeTitle",
          "Manual audio recording complete",
        ),
      );
    }

    return { chips, messages };
  }

  if (row.variant === RESULT_MODE_CONFIG.postExertion.variant) {
    if (showSignalQuality) {
      addChip(
        "respiratory",
        t(
          "result.postExertion.respiratorySignalQuality",
          "Respiratory Signal Quality",
        ),
        respiratoryQuality,
      );
      addMessage(
        "respiratory-quality",
        getRespiratoryQualityMessage(
          normalizePassFailStatus(respiratoryQuality),
          t,
        ),
      );
    }

    return { chips, messages };
  }

  const cardiacQualityMessage = showCardiacQuality
    ? getHeartRateQualityMessage(row.hrQuality, isPassingHrSignalQuality, t)
    : null;
  const respiratoryQualityMessage = showSignalQuality
    ? getRespiratoryQualityMessage(normalizePassFailStatus(respiratoryQuality), t)
    : null;

  if (showCardiacQuality) {
    addChip(
      "cardiac",
      t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
      row.hrQuality,
    );
  }

  if (showSignalQuality) {
    addChip(
      "respiratory",
      t("result.cardiopulmonary.respiratoryQuality", "Respiratory Quality"),
      respiratoryQuality,
    );
  }

  // The chips above already name which signal(s) failed, so collapse multiple
  // failures into a single non-repetitive guidance line rather than one
  // "… Please try again." per metric. A single failure keeps its specific copy.
  const qualityFailMessages = [
    cardiacQualityMessage,
    respiratoryQualityMessage,
  ].filter(Boolean);
  if (qualityFailMessages.length === 1) {
    addMessage("quality-guidance", qualityFailMessages[0]);
  } else if (qualityFailMessages.length > 1) {
    addMessage("quality-guidance", {
      title: t("result.cardiopulmonary.qualityFailTitle", "Low signal quality."),
      subtitle: t(
        "result.cardiopulmonary.qualityFailSubtitle",
        "Please try again.",
      ),
    });
  }

  return { chips, messages };
}

function buildResultCardMeta(row) {
  return {
    meta: row?.time || row?.date || "",
    secondaryMeta: row?.time && row?.date ? row.date : "",
  };
}

/**
 * Maps a measurement row into the view model consumed by the results list
 * (label, date/time meta, mode icon, quality chips, and the detail-page link).
 * Returns null for rows without an id or a recognised recording mode.
 */
export function buildResultListItem(
  row,
  t,
  resultFeatures = null,
  qualityStatusPillsEnabled = false,
) {
  const config = getResultModeConfigForVariant(row?.variant);
  const workflowStage =
    typeof row?.rawMeasurement?.workflow?.stage === "string"
      ? row.rawMeasurement.workflow.stage.trim().toLowerCase()
      : null;

  if (!row?.id || !config) {
    return null;
  }

  if (
    row.variant === RESULT_MODE_CONFIG.sixMinuteWalk.variant &&
    workflowStage === "pre"
  ) {
    return null;
  }

  const { meta, secondaryMeta } = buildResultCardMeta(row);

  return {
    id: `${row.variant}:${row.id}`,
    label: getResultModeLabel(row.variant, t),
    meta,
    secondaryMeta,
    details: buildResultQualityDetails(
      row,
      t,
      resultFeatures,
      qualityStatusPillsEnabled,
    ),
    icon: getResultModeIcon(row.variant),
    to: buildResultPath({
      variant: row.variant,
      docid: row.id,
      context: config.context,
      tab: config.tab,
    }),
  };
}
